import firebase from 'firebase';
import { Config } from '../config';
import CacheService from './CacheService';
import { EmployeeDocument } from '../models/models';

// Backend API base URL
const API_BASE_URL = Config.Env.ApiBaseUrl

class NetworkService {
  private cacheService: CacheService;
  private static pendingRequests: Map<string, Promise<any>> = new Map();

  constructor() {
    this.cacheService = CacheService.getInstance();
  }

  /**
   * Sends an estimate email to a customer
   * @param {string} customerEmail - The email address of the customer
   * @param {string} customerName - The name of the customer
   * @param {string} estimateId - The ID of the estimate
   * @param {string} message - Optional customer message to include in the email
   * @param {string} customerPhone - Optional customer phone number for SMS
   * @returns {Promise<any>} A promise that resolves to the API response
   */
  sendEstimateEmail(customerEmail: string, customerName: string, estimateId: string, message?: string, customerPhone?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      fetch(`${API_BASE_URL}/estimates/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          customerEmail,
          customerName,
          estimateId,
          message, // Include customer message in the request
          customerPhone // Include customer phone for SMS
        }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP Error: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          // Log to console instead of analytics
          console.log("Estimate email sent:", {
            customerEmail,
            estimateId,
            hasMessage: !!message,
            hasSMS: !!customerPhone
          });
          resolve(data);
        })
        .catch((error) => {
          console.error("Error sending estimate email:", error);
          reject(error);
        });
    });
  }

  /**
   * Creates an invoice from an appointment and sends it to the customer
   * @param {any} appointment - The appointment data
   * @param {any} client - The client data
   * @returns {Promise<any>} A promise that resolves to the API response with the invoice ID
   */
  /**
   * Creates an invoice for an appointment without sending it
   * @param {any} appointment - The appointment to create an invoice for
   * @param {any} client - The client the invoice is for
   * @returns {Promise<any>} A promise that resolves to the API response with the invoice ID
   */
  createInvoice(appointment: any, client: any): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        // Get the auth token
        const token = await firebase.auth().currentUser?.getIdToken();
        
        if (!appointment.id) {
          throw new Error('Appointment ID is required');
        }

        fetch(`${API_BASE_URL}/appointments/${appointment.id}/invoice/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        })
          .then((response) => {
            if (!response.ok) {
              return response.json().then(err => {
                throw new Error(err.message || `HTTP Error: ${response.status}`);
              });
            }
            return response.json();
          })
          .then((data) => {
            console.log("Invoice created:", {
              appointmentId: appointment.id,
              clientId: client.id,
              invoiceId: data.data?.invoiceId
            });
            resolve(data);
          })
          .catch((error) => {
            console.error("Error creating invoice:", error);
            reject(error);
          });
      } catch (error) {
        console.error("Error getting auth token:", error);
        reject(error);
      }
    });
  }

  createAndSendInvoice(appointment: any, client: any): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        // Get the auth token
        const token = await firebase.auth().currentUser?.getIdToken();
        
        if (!appointment.id) {
          throw new Error('Appointment ID is required');
        }

        fetch(`${API_BASE_URL}/appointments/${appointment.id}/invoice/create-and-send`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        })
          .then((response) => {
            if (!response.ok) {
              return response.json().then(err => {
                throw new Error(err.message || `HTTP Error: ${response.status}`);
              });
            }
            return response.json();
          })
          .then((data) => {
            console.log("Invoice created and sent:", {
              appointmentId: appointment.id,
              clientId: client.id,
              invoiceId: data.data?.invoiceId
            });
            resolve(data);
          })
          .catch((error) => {
            console.error("Error creating and sending invoice:", error);
            reject(error);
          });
      } catch (error) {
        console.error("Error getting auth token:", error);
        reject(error);
      }
    });
  }

  /**
   * Generic GET request with authentication
   */
  async get(url: string, options?: { responseType?: 'json' | 'blob' }): Promise<any> {
    const user = firebase.auth().currentUser;
    const token = user ? await user.getIdToken() : null;
    
    console.log('🔐 [NetworkService] GET request to:', `${API_BASE_URL}${url}`);
    console.log('👤 [NetworkService] Current user:', user ? `${user.email} (${user.uid})` : 'No user');
    console.log('🔑 [NetworkService] Has token:', !!token);

    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    console.log('📨 [NetworkService] Response status:', response.status);
    
    if (!response.ok) {
      let errorMessage = `HTTP Error: ${response.status}`;
      let errorCode: string | undefined;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
        // Capture error code if present
        if (errorData.code) {
          errorCode = errorData.code;
        }
      } catch {
        // If parsing JSON fails, try to get text
        const errorText = await response.text();
        if (errorText) {
          errorMessage = `${errorMessage} - ${errorText}`;
        }
      }
      console.error('❌ [NetworkService] HTTP Error:', response.status, errorMessage, errorCode);
      const error = new Error(errorMessage);
      (error as any).status = response.status;
      if (errorCode) {
        (error as any).code = errorCode;
      }
      throw error;
    }

    if (options?.responseType === 'blob') {
      const blob = await response.blob();
      return { data: blob };
    }

    const result = await response.json();
    console.log('📊 [NetworkService] Response data:', result);
    return result;
  }

  /**
   * Get the base URL for the API
   */
  getBaseUrl(): string {
    return API_BASE_URL;
  }

  /**
   * Generic POST request with authentication
   */
  async post(url: string, data?: any): Promise<any> {
    const user = firebase.auth().currentUser;
    const token = user ? await user.getIdToken() : null;

    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      let errorMessage = `HTTP Error: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch {
        // If parsing JSON fails, try to get text
        const errorText = await response.text();
        if (errorText) {
          errorMessage = `${errorMessage} - ${errorText}`;
        }
      }
      const error = new Error(errorMessage);
      (error as any).status = response.status;
      throw error;
    }

    return await response.json();
  }

  /**
   * Generic PUT request with authentication
   */
  async put(url: string, data?: any): Promise<any> {
    const user = firebase.auth().currentUser;
    const token = user ? await user.getIdToken() : null;

    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      let errorMessage = `HTTP Error: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch {
        // If parsing JSON fails, try to get text
        const errorText = await response.text();
        if (errorText) {
          errorMessage = `${errorMessage} - ${errorText}`;
        }
      }
      const error = new Error(errorMessage);
      (error as any).status = response.status;
      throw error;
    }

    return await response.json();
  }

  /**
   * Generic DELETE request with authentication
   */
  async delete(url: string): Promise<any> {
    const user = firebase.auth().currentUser;
    const token = user ? await user.getIdToken() : null;

    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      let errorMessage = `HTTP Error: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch {
        // If parsing JSON fails, try to get text
        const errorText = await response.text();
        if (errorText) {
          errorMessage = `${errorMessage} - ${errorText}`;
        }
      }
      const error = new Error(errorMessage);
      (error as any).status = response.status;
      throw error;
    }

    return await response.json();
  }

  /**
   * Generic PATCH request with authentication
   */
  async patch(url: string, data?: any): Promise<any> {
    const user = firebase.auth().currentUser;
    const token = user ? await user.getIdToken() : null;

    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      let errorMessage = `HTTP Error: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch {
        // If parsing JSON fails, try to get text
        const errorText = await response.text();
        if (errorText) {
          errorMessage = `${errorMessage} - ${errorText}`;
        }
      }
      const error = new Error(errorMessage);
      (error as any).status = response.status;
      throw error;
    }

    return await response.json();
  }

  /**
   * POST request with FormData (for file uploads)
   */
  async postFormData(url: string, formData: FormData): Promise<any> {
    const user = firebase.auth().currentUser;
    const token = user ? await user.getIdToken() : null;

    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: {
        // Don't set Content-Type for FormData - let the browser set it with boundary
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      let errorMessage = `HTTP Error: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch {
        // If parsing JSON fails, try to get text
        const errorText = await response.text();
        if (errorText) {
          errorMessage = `${errorMessage} - ${errorText}`;
        }
      }
      const error = new Error(errorMessage);
      (error as any).status = response.status;
      throw error;
    }

    return await response.json();
  }

  /**
   * Get active employees with caching and request deduplication
   * This method caches the result for 5 minutes and ensures only one API call is made at a time
   */
  async getActiveEmployees(): Promise<{ data: EmployeeDocument[] }> {
    const requestKey = 'request:employees:active';

    // Check cache first
    const cachedEmployees = this.cacheService.getActiveEmployees();
    if (cachedEmployees) {
      console.log('🗃️ Returning cached active employees');
      return { data: cachedEmployees };
    }

    // Check if there's already a pending request
    if (NetworkService.pendingRequests.has(requestKey)) {
      console.log('⏳ Waiting for existing active employees request...');
      try {
        const result = await NetworkService.pendingRequests.get(requestKey);
        this.cacheService.setActiveEmployees(result.data);
        return result;
      } catch (error) {
        // If the pending request failed, remove it so we can try again
        NetworkService.pendingRequests.delete(requestKey);
        throw error;
      }
    }

    // Create new request
    console.log('🚀 Fetching active employees from API (new request)');
    const requestPromise = this.fetchActiveEmployeesFromAPI();
    
    // Store the promise so other calls can wait for it
    NetworkService.pendingRequests.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Clean up the pending request
      NetworkService.pendingRequests.delete(requestKey);
    }
  }

  /**
   * Internal method to fetch employees from API
   */
  private async fetchActiveEmployeesFromAPI(): Promise<{ data: EmployeeDocument[] }> {
    console.log('🔍 Fetching active employees from API: /employees?isActive=true');
    const response = await this.get('/employees?isActive=true');
    console.log('📊 Active employees API response:', response);
    
    // Cache the result
    if (response.data) {
      this.cacheService.setActiveEmployees(response.data);
      console.log(`✅ Cached ${response.data.length} active employees`);
    } else {
      console.warn('⚠️ No employee data in response');
    }

    return response;
  }

  /**
   * Invalidate the employees cache
   * Call this when employees data might have changed
   */
  invalidateEmployeesCache(): void {
    this.cacheService.invalidateEmployees();
    // Also clear any pending employee requests
    NetworkService.pendingRequests.delete('request:employees:active');
  }

  /**
   * Get customer by ID with caching and request deduplication
   * This method caches customer data for 10 minutes and ensures only one API call per customer is made at a time
   */
  async getCustomerById(customerId: string): Promise<any> {
    const requestKey = `request:customer:${customerId}`;

    // Check cache first
    const cachedCustomer = this.cacheService.getCustomer(customerId);
    if (cachedCustomer) {
      console.log(`🗃️ Returning cached customer data for ID: ${customerId}`);
      return cachedCustomer;
    }

    // Check if there's already a pending request for this customer
    if (NetworkService.pendingRequests.has(requestKey)) {
      console.log(`⏳ Waiting for existing customer request: ${customerId}`);
      try {
        const result = await NetworkService.pendingRequests.get(requestKey);
        return result;
      } catch (error) {
        // If the pending request failed, remove it so we can try again
        NetworkService.pendingRequests.delete(requestKey);
        throw error;
      }
    }

    // Create new request
    console.log(`🚀 Fetching customer from API for ID: ${customerId} (new request)`);
    const requestPromise = this.fetchCustomerFromAPI(customerId);
    
    // Store the promise so other calls can wait for it
    NetworkService.pendingRequests.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Clean up the pending request
      NetworkService.pendingRequests.delete(requestKey);
    }
  }

  /**
   * Internal method to fetch customer from API
   */
  private async fetchCustomerFromAPI(customerId: string): Promise<any> {
    const response = await this.get(`/crm/customers/${customerId}`);
    
    // Cache the result
    if (response.data) {
      this.cacheService.setCustomer(customerId, response.data);
      console.log(`✅ Cached customer data for ID: ${customerId}`);
      return response.data;
    }

    return null;
  }

  /**
   * Invalidate customer cache for a specific customer
   * Call this when customer data has been updated
   */
  invalidateCustomerCache(customerId: string): void {
    this.cacheService.invalidateCustomer(customerId);
    // Also clear any pending customer requests
    NetworkService.pendingRequests.delete(`request:customer:${customerId}`);
  }

  /**
   * Invalidate all customer caches
   * Call this when customer data structure has changed
   */
  invalidateAllCustomerCaches(): void {
    this.cacheService.invalidateAllCustomers();
    // Clear all pending customer requests
    const keysToDelete: string[] = [];
    NetworkService.pendingRequests.forEach((promise, key) => {
      if (key.startsWith('request:customer:')) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => {
      NetworkService.pendingRequests.delete(key);
    });
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { size: number; keys: string[] } {
    const stats = this.cacheService.getStats();
    return {
      size: stats.entryCount,
      keys: stats.keys
    };
  }

  /**
   * Clear all caches
   */
  clearAllCaches(): void {
    this.cacheService.clear();
    NetworkService.pendingRequests.clear();
  }
}

export default NetworkService;